import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { cacheTiers } from "@/api/queryClient";
import { qk } from "@/api/queryKeys";
import { useApiFetcher } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import {
  getCharlieDrillDown,
  getCharlieFeed,
  postCharlieTaskNote,
  postCharlieTaskReschedule,
  postCharlieTaskStatus,
} from "@/service/reqUrl.service";
import { useStore } from "@/provider/store";

import { MockProvider } from "./charlieMock";
import type {
  CharlieDrillParams,
  CharlieFeed,
  CharlieFeedParams,
  CharlieProvider,
  CharlieRecords,
  CharlieService,
  CharlieTask,
  Suggestion,
  TaskStatus,
} from "../types";

/** The four states `[Charlie].[ChangeTaskStatus]` accepts. */
type SpTaskStatus = "Accepted" | "Rejected" | "Cancelled" | "Completed";

/** One row of either list in the `[Charlie].[GetTasks]` payload. The two differ
 *  only in the assignment columns, so they share a shape. */
interface FeedRow {
  SignalTaskID: number;
  BranchSystemID?: string | null;
  SignalID?: number | null;
  SignalDescription?: string | null;
  /** What the records behind this task are — "Orders", "Patients" … The brief
   *  reads it straight after "Open". Absent when there is nothing to drill. */
  SignalDrillDownType?: string | null;
  /** What the brief groups on. */
  SignalCategoryName?: string | null;
  TaskTitle?: string | null;
  TaskStatus?: string | null;
  TaskDueDate?: string | null;
  TaskAcceptedDate?: string | null;
  TaskCancelReason?: string | null;
  CreatedAt?: string | null;
  Notes?: { SignalNote?: string | null }[] | null;
}

interface FeedResponse {
  UnassignedTasks?: FeedRow[];
  AssignedTasks?: FeedRow[];
}

/** The SP sends full timestamps ("2026-09-08T12:42:09.580"); every date the UI
 *  renders is a calendar day. Sliced rather than parsed, so a date-only value
 *  can't slide a day backwards for a viewer west of UTC. */
const dayOf = (raw: string | null | undefined): string =>
  String(raw ?? "").slice(0, 10);

/** `TaskStatus` from the SP → the three states My Tasks tabs on. Anything not
 *  recognised counts as open: an unknown status is still the user's problem,
 *  and hiding it would lose the task entirely. */
const taskStatusOf = (raw: string | null | undefined): TaskStatus => {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "completed" || s === "complete") return "complete";
  if (s === "cancelled" || s === "canceled") return "cancelled";
  return "open";
};

/** The scoping IDs every drill row repeats. They say what was ASKED for, not
 *  what was found, so they are not columns anyone needs to read. */
const SCOPE_COLUMNS = new Set(["CustomerSystemID", "ServiceLineSystemID", "BranchSystemID"]);

/** A SQL column name as a header: "PhysicianName" → "Physician Name",
 *  "OrderID" → "Order ID", "SOCDate" → "SOC Date". Acronym runs stay whole,
 *  which is the whole reason this isn't a single regex. */
const columnLabel = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .trim();

/** One cell as text. Timestamps are cut to the calendar day — every date the
 *  drill shows is a day, and the time part is noise the columns can't fit. */
const cell = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /^\d{4}-\d{2}-\d{2}T/.test(s) ? s.slice(0, 10) : s;
};

/** The cache key, as the query string that fetches it — one source of truth for
 *  both, so an entry can never be keyed on parameters it wasn't fetched with.
 *  Empty values are DROPPED, not sent blank: an absent `branchSystemID` is what
 *  the SP reads as "all my branches". */
const queryString = (params: Record<string, string>): string =>
  new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== "" && v != null),
  ).toString();

/** Today, as the SP wants its date stamps. Local calendar day, not UTC — a
 *  decision made on the evening of the 8th is the 8th's. */
const todayIso = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Charlie's data source.
 *
 * The brief and My Tasks come from `GET /charlie/feed` — one call, both halves,
 * so the two panels can never disagree about a task just accepted. The payor
 * board still falls through to `MockProvider`, because its endpoint doesn't
 * exist yet; that is why this spreads the mock rather than replacing it.
 *
 * With no service line resolved (config hasn't landed) there is nothing to
 * query with, so the whole screen falls back to the mock rather than firing a
 * call that must fail.
 *
 * ── Caching (TanStack Query, tier `charlie`: 15 min) ────────────────────────
 * Both reads go through `queryClient.fetchQuery`, so they sit in the SAME cache
 * as the rest of the app, under the same `qk` keys, and honour a 15-minute
 * `staleTime`: a repeat within the window is served from memory with no request
 * at all, and two callers asking at once share one in-flight promise.
 *
 * `fetchQuery` rather than `useApiQuery`, which is what the dashboard hooks use:
 * Charlie's reads are IMPERATIVE — the feed is fetched by an effect that also
 * owns the loading and error bars, and a drill-down is fetched on a click, for
 * whichever task was clicked. A hook cannot be called from either place, and
 * the provider indirection (which is what lets the payor board stay on the mock
 * while the rest went live) is not a hook boundary. Same client, same cache,
 * same error semantics — only the call site differs.
 *
 * Writes are not cached, but they DO invalidate the feed, which is the half of
 * caching that keeps a task from coming back after it was decided.
 */
export function useCharlieProvider(): CharlieProvider {
  // Throws on a 4xx/5xx that the axios interceptor resolves — `queryFn` results
  // are only trustworthy if a failed response never becomes cached "data".
  const fetcher = useApiFetcher();
  const queryClient = useQueryClient();
  const userKey = useUserKey();
  // `branchOptions`, not the store's `branchIdFor`/`branchLabelFor` helpers:
  // those are re-created on every render, and this provider is a dependency of
  // Charlie's fetch effect — a new identity per render would refetch forever.
  const { serviceLineSystemID, branchOptions } = useStore();

  return useMemo<CharlieProvider>(() => {
    if (!serviceLineSystemID) return MockProvider;

    // The SP scopes by BranchSystemID; the dropdown works in labels. "all"
    // sends nothing, which the SP reads as every branch this user may see.
    const idOf = (label: string): string =>
      branchOptions.find((b) => b.label === label)?.value ?? "";
    // An unmapped ID falls back to itself rather than blanking the chip — the
    // row is still real, it just belongs to a branch the config didn't list.
    const branchNameOf = (id: string | null | undefined): string => {
      const raw = String(id ?? "");
      return branchOptions.find((b) => b.value === raw)?.label ?? raw;
    };

    const toSuggestion = (row: FeedRow, svc: CharlieService): Suggestion => {
      const category = String(row.SignalCategoryName ?? "").trim() || "Other";
      const drillType = String(row.SignalDrillDownType ?? "").trim();
      const due = dayOf(row.TaskDueDate);
      return {
        id: String(row.SignalTaskID),
        svc,
        bucket: category,
        bucketLabel: category,
        branch: branchNameOf(row.BranchSystemID),
        title: String(row.TaskTitle ?? ""),
        signal: String(row.SignalDescription ?? ""),
        // A dated task is a commitment; an undated one is a nudge. That is the
        // only deadline signal the SP carries today.
        deadline: due ? "hard" : "soft",
        defaultDue: due || dayOf(new Date().toISOString()),
        // The rows themselves are fetched on the click — see `drillDown`. This
        // stays empty so nothing renders a half-built table before then.
        records: { title: String(row.TaskTitle ?? ""), subtitle: category, rows: [] },
        // No drill type means this signal has no records behind it, and
        // `SuggestionItem` then shows no button at all.
        ...(drillType
          ? {
              drill: {
                type: drillType,
                signalID: String(row.SignalID ?? ""),
                signalTaskID: String(row.SignalTaskID),
                // The row's OWN branch: the screen can be on "all", and the SP
                // wants one branch.
                branchSystemID: String(row.BranchSystemID ?? ""),
              },
            }
          : {}),
      };
    };

    const toTask = (row: FeedRow, svc: CharlieService): CharlieTask => ({
      id: String(row.SignalTaskID),
      svc,
      branch: branchNameOf(row.BranchSystemID),
      bucketLabel: String(row.SignalCategoryName ?? "").trim() || "Other",
      title: String(row.TaskTitle ?? ""),
      // Every note on the task, oldest first — the SP returns the thread, and
      // My Tasks renders a single quoted line.
      notes: (row.Notes ?? [])
        .map((n) => String(n?.SignalNote ?? "").trim())
        .filter(Boolean)
        .join(" · "),
      // Only a real due date. The accepted date is not a deadline, and putting
      // it in the due slot would read as one.
      due: dayOf(row.TaskDueDate),
      status: taskStatusOf(row.TaskStatus),
      // Survives a reload: My Tasks shows why a cancelled task was dropped.
      ...(row.TaskCancelReason ? { cancelReason: String(row.TaskCancelReason) } : {}),
      createdAt: dayOf(row.CreatedAt),
    });

    /** Every write is a POST that names a SignalTaskID and returns nothing the
     *  UI needs — so they share one caller that only has to decide whether the
     *  server said yes. A failed write throws (that is what `useApiFetcher`
     *  adds over `requestToServer`), and the caller leaves the screen untouched
     *  rather than showing a change the database didn't take.
     *
     *  Every write also drops the cached feed. Not a refetch: the feed has no
     *  live observer — Charlie fetches it imperatively — so this only marks the
     *  entries stale, and the next read (a service-line or branch change, or a
     *  remount) goes to the server. That is exactly the timing the brief was
     *  written for: an accepted item stays on screen, greyed, and leaves on the
     *  next fetch, by which time the SP has moved it into the assigned list.
     *
     *  Drill-down entries are deliberately left alone — deciding a task does
     *  not change the records that raised it. */
    const post = async (url: string, body: Record<string, unknown>): Promise<void> => {
      await fetcher<unknown>(url, body, "POST", "Charlie couldn't save that");
      await queryClient.invalidateQueries({ queryKey: qk.charlieFeeds(userKey) });
    };

    const setStatus = (id: string, status: SpTaskStatus, reason?: string) =>
      post(postCharlieTaskStatus, {
        signalTaskID: Number(id),
        taskStatus: status,
        taskStatusDate: todayIso(),
        // The SP wants a reason for the two states that discard work; the other
        // two send none rather than an empty string.
        ...(reason ? { taskStatusReason: reason } : {}),
      });

    return {
      ...MockProvider,
      name: "api",
      feed: async ({ svc, branch }): Promise<CharlieFeed> => {
        // "all" sends no branch, which the SP reads as every branch this user
        // may see. The KEY carries the SystemID, never the label.
        const key: CharlieFeedParams = {
          serviceLineSystemID,
          branchSystemID: branch === "all" ? "" : idOf(branch),
        };
        const payload = await queryClient.fetchQuery({
          queryKey: qk.charlieFeed(userKey, key),
          queryFn: () =>
            fetcher<FeedResponse | null>(
              `${getCharlieFeed}?${queryString(key)}`,
              null,
              "GET",
              "Charlie's feed is unavailable",
            ),
          ...cacheTiers.charlie,
        });
        // Mapped OUTSIDE the queryFn, so the cache holds the SP's own payload:
        // the branch LABELS this attaches come from the config, which can land
        // after the feed does, and caching them would freeze a row's chip to
        // whatever the config knew at fetch time.
        return {
          suggestions: (payload?.UnassignedTasks ?? []).map((r) => toSuggestion(r, svc)),
          tasks: (payload?.AssignedTasks ?? []).map((r) => toTask(r, svc)),
        };
      },

      // Accepting is three writes, in order: the decision itself, then the
      // administrator's own context. Status goes first because it is the one
      // that must land — a note or a due date on a task nobody owns is noise.
      acceptSuggestion: async (sug, { notes, due }) => {
        await setStatus(sug.id, "Accepted");
        if (notes.trim()) await post(postCharlieTaskNote, { signalTaskID: Number(sug.id), taskNote: notes.trim() });
        if (due) await post(postCharlieTaskReschedule, { signalTaskID: Number(sug.id), taskDueDate: due });
        return {
          id: sug.id,
          svc: sug.svc,
          branch: sug.branch,
          bucketLabel: sug.bucketLabel,
          title: sug.title,
          notes: notes.trim(),
          due,
          status: "open",
          createdAt: todayIso(),
        };
      },

      rejectSuggestion: async (id, reason) => {
        await setStatus(id, "Rejected", reason);
        return { id, reason };
      },

      // The records behind one task — what "Open Orders" opens. Fetched here
      // rather than with the feed because most tasks are never drilled into,
      // and the columns differ per signal, so there is nothing to merge.
      drillDown: async (sug): Promise<CharlieRecords> => {
        // No drill type on the row: nothing to ask the SP for. The button isn't
        // rendered in that case, so this is only a guard.
        if (!sug.drill) return sug.records;
        const key: CharlieDrillParams = {
          serviceLineSystemID,
          branchSystemID: sug.drill.branchSystemID,
          signalID: sug.drill.signalID,
          signalTaskID: sug.drill.signalTaskID,
        };
        // Re-opening the same task's records inside the 15-minute window is
        // served from the cache — no request, and the modal opens instantly.
        const fetched = await queryClient.fetchQuery({
          queryKey: qk.charlieDrill(userKey, key),
          queryFn: () =>
            fetcher<Record<string, unknown>[] | null>(
              `${getCharlieDrillDown}?${queryString(key)}`,
              null,
              "GET",
              "Charlie couldn't open those records",
            ),
          ...cacheTiers.charlie,
        });
        const rows = Array.isArray(fetched)
          ? fetched.filter((r) => r && typeof r === "object")
          : [];
        // Columns come from the data: each drill type has its own set, and the
        // UI holds no list of them. Union of every row's keys, so a row with a
        // null column the others carry still lines up.
        const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r)))).filter(
          (k) => !SCOPE_COLUMNS.has(k),
        );
        return {
          title: sug.title,
          subtitle: [sug.drill.type, sug.branch, `${rows.length} record${rows.length === 1 ? "" : "s"}`]
            .filter(Boolean)
            .join(" · "),
          columns: keys.map(columnLabel),
          rows: rows.map((r) => keys.map((k) => cell(r[k]))),
        };
      },

      // My Tasks sends one field at a time: a status change, or a new due date.
      updateTask: async (id, patch) => {
        if (patch.due) await post(postCharlieTaskReschedule, { signalTaskID: Number(id), taskDueDate: patch.due });
        if (patch.status === "complete") await setStatus(id, "Completed");
        // Re-opening a completed task puts it back where accepting left it.
        if (patch.status === "open") await setStatus(id, "Accepted");
        if (patch.status === "cancelled") await setStatus(id, "Cancelled", patch.cancelReason);
        return undefined;
      },
    };
    // All four are stable across renders: `fetcher` is a `useCallback`,
    // `queryClient` is the app's singleton and `userKey` is memoized on the
    // auth identity — so the provider only changes when the service line or the
    // branch list does, and Charlie's fetch effect doesn't loop.
  }, [serviceLineSystemID, branchOptions, fetcher, queryClient, userKey]);
}
